/**
 * Map the descriptive names used internally to the compact names exposed in
 * browser URLs. Internal names remain unchanged throughout the application.
 */
export const PARAM_MAP = Object.freeze({
  ticketmasterId: 'eid',
  ticketmasterAttractionId: 'aid',
  ticketmasterVenueId: 'vid',
  shareTribeListings: 'lid',
  attractionImageUrl: 'img',
  attractionId: 'sid',
  eventName: 'q',
  eventDate: 'd',
  eventTime: 't',
  venue: 'v',
  location: 'loc',
  keywords: 'k',
  intent: 'i',
});

const REVERSE_PARAM_MAP = Object.keys(PARAM_MAP).reduce((result, internalName) => {
  result[PARAM_MAP[internalName]] = internalName;
  return result;
}, {});

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const toEntries = source => {
  if (typeof URLSearchParams !== 'undefined' && source instanceof URLSearchParams) {
    return Array.from(source.entries());
  }

  if (typeof source === 'string') {
    const query = source.startsWith('?') ? source.slice(1) : source;
    return Array.from(new URLSearchParams(query).entries());
  }

  if (!source || typeof source !== 'object') {
    return [];
  }

  return Object.entries(source).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map(item => [key, item]);
    }
    return [[key, value]];
  });
};

const toStringValue = value => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const splitListingIds = value => {
  if (Array.isArray(value)) {
    return value.flatMap(item => splitListingIds(item));
  }

  return toStringValue(value)
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);
};

const encodeBase64Url = value => {
  let base64;

  if (typeof btoa === 'function') {
    const binary = encodeURIComponent(value).replace(/%([0-9A-F]{2})/gi, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
    base64 = btoa(binary);
  } else if (typeof Buffer !== 'undefined') {
    base64 = Buffer.from(value, 'utf8').toString('base64');
  } else {
    throw new Error('Base64 encoding is not available in this environment');
  }

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const decodeBase64Url = value => {
  const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

  if (typeof atob === 'function') {
    const binary = atob(padded);
    const encoded = Array.from(binary, character =>
      `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    ).join('');
    return decodeURIComponent(encoded);
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(padded, 'base64').toString('utf8');
  }

  throw new Error('Base64 decoding is not available in this environment');
};

/**
 * Remove a vendor prefix before putting a listing ID in the URL.
 * Native Sharetribe UUIDs are left untouched.
 */
export const maskListingId = id => {
  const value = toStringValue(id).trim();
  return /^seatgeek-/i.test(value) ? value.slice('seatgeek-'.length) : value;
};

/**
 * Restore the known SeatGeek prefix for compact numeric listing IDs. Native
 * Sharetribe UUIDs and other opaque IDs remain unchanged.
 */
export const unmaskListingId = id => {
  const value = toStringValue(id).trim();
  if (!value || /^seatgeek-/i.test(value)) {
    return value;
  }

  return /^\d+$/.test(value) ? `seatgeek-${value}` : value;
};

export const encodeImageUrl = url => {
  const value = toStringValue(url);
  if (!value) return value;

  try {
    return encodeBase64Url(value);
  } catch (error) {
    return value;
  }
};

export const decodeImageUrl = encoded => {
  const value = toStringValue(encoded);
  if (!value) return value;

  try {
    const decoded = decodeBase64Url(value);
    return /^(?:https?:\/\/|data:|\/(?!\/))/i.test(decoded) ? decoded : value;
  } catch (error) {
    // Old URLs contain the raw image URL. Leave malformed/new unknown values
    // untouched so they continue to work as gracefully as possible.
    return value;
  }
};

/**
 * Convert internal parameter names to their masked URL representation.
 */
export const maskParams = internalParams => {
  const masked = new URLSearchParams();

  toEntries(internalParams).forEach(([internalName, rawValue]) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return;
    }

    const maskedName = PARAM_MAP[internalName] || internalName;
    if (internalName === 'shareTribeListings') {
      const ids = splitListingIds(rawValue).map(maskListingId);
      if (ids.length > 0) {
        masked.set(maskedName, ids.join(','));
      }
      return;
    }

    const value = internalName === 'attractionImageUrl' ? encodeImageUrl(rawValue) : rawValue;
    if (Array.isArray(value)) {
      value.forEach(item => masked.append(maskedName, toStringValue(item)));
    } else {
      masked.set(maskedName, toStringValue(value));
    }
  });

  return masked;
};

/**
 * Convert masked URL parameters back to the internal names used by the app.
 * Both masked and legacy names are accepted; masked values take precedence
 * when a URL contains both versions.
 */
export const unmaskParams = source => {
  const unmasked = {};
  const priorities = {};

  toEntries(source).forEach(([rawName, rawValue]) => {
    const isMaskedName = hasOwn(REVERSE_PARAM_MAP, rawName);
    const internalName = isMaskedName ? REVERSE_PARAM_MAP[rawName] : rawName;
    const priority = isMaskedName ? 2 : 1;

    if (priorities[internalName] > priority) {
      return;
    }

    let value = rawValue;
    if (isMaskedName && internalName === 'shareTribeListings') {
      value = splitListingIds(rawValue).map(unmaskListingId).join(',');
    } else if (isMaskedName && internalName === 'attractionImageUrl') {
      value = decodeImageUrl(rawValue);
    }

    if (priorities[internalName] < priority || !hasOwn(unmasked, internalName)) {
      unmasked[internalName] = value;
      priorities[internalName] = priority;
      return;
    }

    const existing = unmasked[internalName];
    unmasked[internalName] = Array.isArray(existing)
      ? [...existing, value]
      : [existing, value];
  });

  return unmasked;
};
